/**
 * App-wide image-preview host.
 *
 * Owns a single {@link ImagePreview} lightbox and exposes an
 * `openImagePreview(url, label)` function via context, so every product
 * thumbnail — listing cells, expanded rows, slide-overs, mobile cards,
 * modals — opens the same full-screen viewer. Mounted once in
 * `AppProviders`; read the opener with {@link useImagePreviewOptional}
 * (thumbnails degrade to a plain image where no host is mounted, e.g. in
 * isolated tests) or {@link useImagePreview} where the host is guaranteed.
 */
import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

import { ImagePreview, type PreviewImage } from './ImagePreview';

/** Opens the shared preview for a real image URL + its caption (SKU). */
export type OpenImagePreview = (url: string, label: string) => void;

/** `undefined` = no provider — the house sentinel, matching AuthContext et al. */
const ImagePreviewContext = createContext<OpenImagePreview | undefined>(undefined);

/**
 * Returns the shared preview opener, throwing outside an
 * {@link ImagePreviewProvider} — the house context shape, so a tree that
 * forgets the provider fails at the missing boundary rather than silently
 * losing click-to-preview.
 */
export function useImagePreview(): OpenImagePreview {
  const open = useContext(ImagePreviewContext);
  if (open === undefined) {
    throw new Error(
      '[ImagePreview] useImagePreview must be used within ImagePreviewProvider — use useImagePreviewOptional() for components that render outside it',
    );
  }
  return open;
}

/**
 * Opt-in variant that returns `null` outside the provider instead of
 * throwing — for thumbnails that render in isolation (tests, storybook-style
 * fixtures) and must degrade to a plain, non-clickable image.
 */
export function useImagePreviewOptional(): OpenImagePreview | null {
  return useContext(ImagePreviewContext) ?? null;
}

export function ImagePreviewProvider({
  children,
}: Readonly<{ children: React.ReactNode }>): React.ReactElement {
  const [image, setImage] = useState<PreviewImage | null>(null);

  const open = useCallback<OpenImagePreview>((url, label) => {
    if (typeof url === 'string' && url.length > 0) setImage({ url, label });
  }, []);
  const close = useCallback(() => setImage(null), []);
  // Stable identity — consumers re-render only when the opener changes (never).
  const value = useMemo(() => open, [open]);

  return (
    <ImagePreviewContext.Provider value={value}>
      {children}
      <ImagePreview image={image} onClose={close} />
    </ImagePreviewContext.Provider>
  );
}

export default ImagePreviewProvider;
